import { gql } from '@apollo/client';
import { createApi } from '@reduxjs/toolkit/query/react';

import { apolloBaseQuery } from './apollo-base-query';

// Types for the GraphQL operations
export type DocumentContextType = 'CONTROL' | 'CARD';

// Document type for compatibility
export type Document = FileMetadata;

export interface DocumentContext {
  type: DocumentContextType;
  controlId?: string;
  cardId?: string;
  fieldId?: string;
  metadata?: Record<string, any>;
}

export interface FileUploadResult {
  success: boolean;
  fileUrl?: string;
  fileName?: string;
  size?: number;
  mimeType?: string;
  metadata?: Record<string, any>;
  error?: string;
  fieldId?: string;
}

export interface BatchDocumentUploadResponse {
  results: FileUploadResult[];
  successCount: number;
  failureCount: number;
}

export interface FileMetadata {
  name: string;
  size: number;
  lastModified: string;
  contentType: string;
  metadata: Record<string, any>;
  url: string;
  downloadUrl: string;
  viewUrl: string;
}

export interface ListFilesResponse {
  files: FileMetadata[];
}

// GraphQL queries and mutations
const BATCH_FILE_UPLOAD = gql`
  mutation BatchFileUpload($input: BatchFileUploadInput!) {
    batchFileUpload(input: $input) {
      results {
        success
        fileUrl
        fileName
        size
        mimeType
        metadata
        error
        fieldId
      }
      successCount
      failureCount
    }
  }
`;

const LIST_FILES = gql`
  query ListFiles($input: ListFilesInput!) {
    listFiles(input: $input) {
      files {
        name
        size
        lastModified
        contentType
        metadata
        url
        downloadUrl
        viewUrl
      }
    }
  }
`;

const DELETE_DOCUMENTS = gql`
  mutation DeleteDocuments($input: DeleteDocumentsInput!) {
    deleteDocuments(input: $input) {
      success
      successCount
      failureCount
      results {
        fileName
        success
        error
      }
    }
  }
`;

// New attachment operations
const ATTACHMENTS_UPLOAD = gql`
  mutation AttachmentsUpload($input: AttachmentsUploadInput!) {
    attachmentsUpload(input: $input) {
      results {
        success
        attachment {
          _id
          fileName
          originalName
          mediaFileId
          uploadedBy
          uploadedAt
          contextType
          controlBundleId
          controlId
          controlIdentifier
          metadata
          createdAt
          updatedAt
        }
        fileName
        error
      }
      successCount
      failureCount
    }
  }
`;

// Note: GET_ATTACHMENT_VIEW query removed - using simple REST API approach for now
// Backend view functionality is preserved for future use

// Define the unified API using GraphQL
export const documentsApi = createApi({
  reducerPath: 'documentsApi',
  baseQuery: apolloBaseQuery(),
  tagTypes: ['Documents'],
  endpoints: (builder) => ({
    // Upload multiple documents in one request (also works for single file)
    uploadBatchDocuments: builder.mutation<
      BatchDocumentUploadResponse,
      {
        files: File[];
        assessmentId: string;
        contexts: DocumentContext[];
        documentType?: string;
      }
    >({
      query: ({ files, assessmentId, contexts, documentType }) => ({
        document: BATCH_FILE_UPLOAD,
        variables: {
          input: {
            files,
            assessmentId,
            contexts,
            documentType,
          },
        },
      }),
      transformResponse: (response: {
        batchFileUpload: BatchDocumentUploadResponse;
      }) => response.batchFileUpload,
    }),

    // List documents with context
    listDocuments: builder.query<
      FileMetadata[],
      {
        assessmentId: string;
        contextType: DocumentContextType;
        controlId?: string;
        cardId?: string;
        fieldId?: string;
        documentType?: string;
      }
    >({
      query: ({
        assessmentId,
        contextType,
        controlId,
        cardId,
        fieldId,
        documentType,
      }) => ({
        document: LIST_FILES,
        variables: {
          input: {
            assessmentId,
            contextType,
            controlId,
            cardId,
            fieldId,
            documentType,
          },
        },
      }),
      transformResponse: (response: { listFiles: ListFilesResponse }) =>
        response.listFiles.files,
    }),

    // Delete documents using GraphQL mutation
    deleteDocuments: builder.mutation<
      {
        success: boolean;
        successCount: number;
        failureCount: number;
        results: { fileName: string; success: boolean; error?: string }[];
      },
      {
        fileNames: string[];
        assessmentId: string;
        context?: {
          type: DocumentContextType;
          controlId?: string;
          cardId?: string;
          fieldId?: string;
          metadata?: Record<string, any>;
        };
      }
    >({
      query: (data) => ({
        document: DELETE_DOCUMENTS,
        variables: {
          input: data,
        },
      }),
      transformResponse: (response: {
        deleteDocuments: {
          success: boolean;
          successCount: number;
          failureCount: number;
          results: {
            fileName: string;
            success: boolean;
            error?: string;
          }[];
        };
      }) => response.deleteDocuments,
    }),

    // New attachment operations
    uploadAttachments: builder.mutation<
      {
        results: {
          success: boolean;
          attachment?: {
            _id: string;
            fileName: string;
            originalName: string;
            uploadedBy: string;
            uploadedAt: string;
            contextType: string;
            controlBundleId: string;
            controlId: string;
            controlIdentifier?: string;
            metadata?: Record<string, any>;
            createdAt?: string;
            updatedAt?: string;
          };
          fileName?: string;
          error?: string;
        }[];
        successCount: number;
        failureCount: number;
      },
      {
        files: File[];
        assessmentId: string;
        contexts: {
          contextType: DocumentContextType;
          controlBundleId: string;
          controlId: string;
          controlIdentifier?: string;
          metadata?: Record<string, any>;
        }[];
      }
    >({
      query: ({ files, assessmentId, contexts }) => ({
        document: ATTACHMENTS_UPLOAD,
        variables: {
          input: {
            files,
            assessmentId,
            contexts,
          },
        },
      }),
      transformResponse: (response: {
        attachmentsUpload: {
          results: {
            success: boolean;
            attachment?: {
              _id: string;
              fileName: string;
              originalName: string;
              uploadedBy: string;
              uploadedAt: string;
              contextType: string;
              controlBundleId: string;
              controlId: string;
              controlIdentifier?: string;
              metadata?: Record<string, any>;
              createdAt?: string;
              updatedAt?: string;
            };
            fileName?: string;
            error?: string;
          }[];
          successCount: number;
          failureCount: number;
        };
      }) => response.attachmentsUpload,
    }),

    // Note: getAttachmentView endpoint removed - using simple REST API approach for now
    // Backend view functionality is preserved for future use
  }),
});

export const {
  useUploadBatchDocumentsMutation,
  useListDocumentsQuery,
  useDeleteDocumentsMutation,
  useUploadAttachmentsMutation,
} = documentsApi;

// Helper function to delete all documents with a specific context
// Helper functions for document operations
export const downloadDocument = (fileName: string): string => {
  return `/api/projects/files/download/${encodeURIComponent(fileName)}`;
};

export const streamDocument = (fileName: string): string => {
  return `/api/projects/files/stream/${encodeURIComponent(fileName)}`;
};

export const deleteAllDocuments = async (
  deleteDocuments: ReturnType<typeof useDeleteDocumentsMutation>[0],
  documents: { fileName: string }[],
  assessmentId: string,
  context?: {
    type: DocumentContextType;
    controlId?: string;
    cardId?: string;
    fieldId?: string;
    metadata?: Record<string, any>;
  }
) => {
  if (!documents || documents.length === 0) {
    return { success: true, successCount: 0, failureCount: 0, results: [] };
  }

  const fileNames = documents.map((doc) => doc.fileName);

  try {
    const result = await deleteDocuments({
      fileNames,
      assessmentId,
      context,
    }).unwrap();

    // Check for empty or undefined results array
    if (!result.results || result.results.length === 0) {
      console.warn('Empty results array in deletion response');
      return {
        success: true,
        successCount: fileNames.length,
        failureCount: 0,
        results: fileNames.map((fileName) => ({
          fileName,
          success: true,
        })),
      };
    }

    return result;
  } catch (error) {
    console.error('Error in deleteAllDocuments:', error);
    return {
      success: false,
      successCount: 0,
      failureCount: fileNames.length,
      results: fileNames.map((fileName) => ({
        fileName,
        success: false,
        error:
          error instanceof Error ? error.message : 'Failed to delete document',
      })),
    };
  }
};
